import { AffiliateLead } from "@core/domain/affiliate-lead/affiliate-lead";
import { CountryCode } from "@settings/countries";
import { PetSex, PetSpecies } from "@settings/pet";
import { LeadError } from "@utils/error-utils/errors";

export interface LeadValuesUpdatable {
  countryIso?: CountryCode;
  petName?: string;
  petSex?: PetSex | null;
  petBreedKbKey?: string | null;
  petSecondBreedKbKey?: string | null;
  email?: string;
  petSpecies?: PetSpecies | null;
  petBirthDate?: Date | null;
  petDiseases?: string[] | null;
  metaFbc?: string | null;
  metaFbp?: string | null;
  petParentZip?: string;
}

export interface LeadDTO {
  countryIso: CountryCode;
  firstUtmMedium: string;
  firstUtmSource: string;
  lastUtmMedium: string;
  lastUtmSource: string;
  petName: string;
  petSex: PetSex | null;
  petBreedKbKey: string | null;
  petSecondBreedKbKey: string | null;
  email: string;
  petParentZip?: string;
  petSpecies: PetSpecies | null;
  petBirthDate: Date | null;
  petBirthDateSetByUser: boolean;
  petDiseases: string[] | null;
  metaFbc?: string | null;
  metaFbp?: string | null;
  affiliate_lead: AffiliateLead | null;
}

export type Lead = LeadDTO;

const initialLead: LeadDTO = {
  countryIso: CountryCode.ES,
  firstUtmMedium: "",
  firstUtmSource: "",
  lastUtmMedium: "",
  lastUtmSource: "",
  petName: "",
  petSpecies: null,
  email: "",
  petBirthDate: null,
  petBirthDateSetByUser: false,
  petBreedKbKey: null,
  petDiseases: null,
  petSecondBreedKbKey: null,
  petSex: null,
  affiliate_lead: null,
};

export const createLead = (data: Partial<LeadDTO>): Lead => {
  return {
    countryIso: data.countryIso ?? initialLead.countryIso,
    firstUtmMedium: data.firstUtmMedium ?? initialLead.firstUtmMedium,
    firstUtmSource: data.firstUtmSource ?? initialLead.firstUtmSource,
    lastUtmMedium: data.lastUtmMedium ?? initialLead.lastUtmMedium,
    lastUtmSource: data.lastUtmSource ?? initialLead.lastUtmSource,
    petName: data.petName ?? initialLead.petName,
    petSpecies: data.petSpecies ?? initialLead.petSpecies,
    email: data.email ?? initialLead.email,
    petBirthDate: data.petBirthDate ?? initialLead.petBirthDate,
    petBirthDateSetByUser: data.petBirthDateSetByUser ?? initialLead.petBirthDateSetByUser,
    petBreedKbKey: data.petBreedKbKey ?? initialLead.petBreedKbKey,
    petDiseases: data.petDiseases ?? initialLead.petDiseases,
    petSecondBreedKbKey: data.petSecondBreedKbKey ?? initialLead.petSecondBreedKbKey,
    petSex: data.petSex ?? initialLead.petSex,
    affiliate_lead: data.affiliate_lead ?? initialLead.affiliate_lead,
  };
};

export const isPetSpeciesValid = (lead: Lead): boolean => {
  return lead.petSpecies !== null && typeof PetSpecies[lead.petSpecies] !== "undefined";
};

export const isPetSexValid = (lead: Lead): boolean => {
  return lead.petSex !== null && typeof PetSex[lead.petSex] !== "undefined";
};

export const isPetBirthDateValid = (lead: Lead): boolean => {
  return lead.petBirthDate instanceof Date && !isNaN(lead.petBirthDate.getTime());
};

export const isPetDiseasesValid = (lead: Lead): boolean => {
  const isValidContent = (diseases: string[]) => diseases.every((disease) => disease !== "");

  return !lead.petDiseases || (Array.isArray(lead.petDiseases) && isValidContent(lead.petDiseases));
};

export const isPetBreedKbKeyValid = (kbKey: string | null): boolean => {
  if (!kbKey) {
    return false;
  }

  const regex = /^[a-z0-9,-]{36}$/;

  return regex.test(kbKey);
};

export const isPetSecondBreedKbKeyValid = (kbKey: string | null): boolean => {
  if (!kbKey) {
    return true;
  }

  const regex = /^[a-z0-9,-]{36}$/;

  return regex.test(kbKey);
};

export const isPetNameValid = (lead: Lead): boolean => {
  return !!lead.petName;
};

export const isEmailValid = (lead: Lead): boolean => {
  const regex = /^[\w+\-.?]+@[a-z\d-]+(?:\.[a-z]+)*\.[a-z]+$/i;

  return regex.test(lead.email);
};

export const isAffiliateLead = (lead: Lead): boolean => {
  return lead.affiliate_lead !== null;
};

export const ensureLeadToCreateResultIsValid = (lead: Lead): void => {
  const validations = [
    { isValid: isPetSpeciesValid(lead), attributeName: "Pet species" },
    { isValid: isPetSexValid(lead), attributeName: "Pet sex" },
    { isValid: isPetNameValid(lead), attributeName: "Pet name" },
    { isValid: isPetBreedKbKeyValid(lead.petBreedKbKey), attributeName: "Pet breed kb key" },
    {
      isValid: isPetSecondBreedKbKeyValid(lead.petSecondBreedKbKey),
      attributeName: "Pet second breed kb key",
    },
    { isValid: isPetBirthDateValid(lead), attributeName: "Pet birthdate" },
    { isValid: isPetDiseasesValid(lead), attributeName: "Pet diseases" },
    { isValid: isEmailValid(lead), attributeName: "Email" },
  ];

  const invalidAttributes = validations
    .filter((validation) => !validation.isValid)
    .map((validation) => validation.attributeName);

  if (invalidAttributes.length > 0) {
    const errorMessage = `${invalidAttributes.join(", ")} ${
      invalidAttributes.length > 1 ? "are" : "is"
    } required.`;

    throw new LeadError(errorMessage);
  }
};
